import { useId, type ChangeEvent } from 'react';
import { formatAmount } from '@lira/shared';
import { cn } from '@/lib/cn';

type MoneyAccent = 'primary' | 'income' | 'expense' | 'cards' | 'balance';

const ACCENT_TEXT: Record<MoneyAccent, string> = {
  primary: 'text-primary',
  income: 'text-income',
  expense: 'text-expense',
  cards: 'text-cards',
  balance: 'text-balance',
};

interface MoneyInputProps {
  /** Valor em CENTAVOS inteiros (fonte de verdade). */
  valueCents: number;
  onChange: (cents: number) => void;
  accent?: MoneyAccent;
  /** 'lg' = valor gigante (display 34px); 'md' = campo normal. */
  size?: 'lg' | 'md';
  autoFocus?: boolean;
  readOnly?: boolean;
  id?: string;
  'aria-label'?: string;
  className?: string;
}

/** Máximo de dígitos para não estourar Number (R$ bilhões cobrem qualquer caso real). */
const MAX_DIGITS = 13;

/**
 * Campo de dinheiro com digitação incremental em centavos (estilo calculadora):
 * digitou "12345" -> exibe "1.234,56". Nunca usa float; o valor sai sempre em centavos.
 */
export function MoneyInput({
  valueCents,
  onChange,
  accent = 'primary',
  size = 'md',
  autoFocus = false,
  readOnly = false,
  id,
  'aria-label': ariaLabel,
  className,
}: MoneyInputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, MAX_DIGITS);
    onChange(digits === '' ? 0 : Number.parseInt(digits, 10));
  };

  const isLg = size === 'lg';

  return (
    <div
      className={cn(
        'flex items-baseline gap-1.5',
        isLg ? 'text-[34px] font-bold' : 'text-[15px] font-semibold',
        className,
      )}
    >
      <span className={cn(ACCENT_TEXT[accent], isLg ? 'text-2xl' : 'text-[15px]')}>R$</span>
      <input
        id={inputId}
        inputMode="decimal"
        autoFocus={autoFocus}
        readOnly={readOnly}
        aria-label={ariaLabel}
        value={formatAmount(valueCents)}
        onChange={handleChange}
        onFocus={(e) => !readOnly && e.target.select()}
        className={cn(
          'w-full min-w-0 bg-transparent tabular-nums outline-none',
          ACCENT_TEXT[accent],
          isLg ? 'placeholder:text-primary/40' : 'placeholder:text-muted',
        )}
      />
    </div>
  );
}
